import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Send } from "lucide-react";
import { API_ENDPOINTS, WS_ENDPOINTS } from "@/constants/constants";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import AdminLayout from "@/components/layout/AdminLayout";

// Interfaces
interface ChatMessage {
  id: string;
  content: string;
  sender: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  createdAt: string;
  channelId?: string;
}

interface Channel {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  members?: number;
}

interface ApiMessage {
  id: string;
  content: string;
  senderId: string;
  senderName: string;
  senderAvatarUrl?: string;
  createdAt: string;
  channelId?: string;
}

interface TypingEvent {
  userId: string;
  userName: string;
  channelId: string;
  isTyping: boolean;
}

const getToken = () => {
  return localStorage.getItem("accessToken");
};

const AdminChat = () => {
  const [user, setUser] = useState(
    JSON.parse(localStorage.getItem("user") || "{}")
  );
  const [channels, setChannels] = useState<Channel[]>([]);
  const [activeChannelId, setActiveChannelId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageInput, setMessageInput] = useState("");
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [isCreatingChannel, setIsCreatingChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelDescription, setNewChannelDescription] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update user state when localStorage changes
  useEffect(() => {
    const updateUser = () => {
      setUser(JSON.parse(localStorage.getItem("user") || "{}"));
    };

    window.addEventListener("storage", updateUser);
    return () => window.removeEventListener("storage", updateUser);
  }, []);

  // Fetch channels on component mount
  useEffect(() => {
    fetchChannels();
  }, []);

  // Connect to WebSocket when component mounts
  useEffect(() => {
    connectWebSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [user?.id]);

  // Load messages when active channel changes
  useEffect(() => {
    if (activeChannelId) {
      fetchChannelMessages(activeChannelId);
    }
  }, [activeChannelId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const connectWebSocket = () => {
    const token = getToken();
    if (!token || !user?.id) return;

    const socket = new WebSocket(`${WS_ENDPOINTS.CHAT.MAIN}?token=${token}`);
    socketRef.current = socket;

    socket.onopen = () => {
      console.log("WebSocket connected");
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === "message" && data.channelId === activeChannelId) {
        const message: ApiMessage = data.payload;
        addMessage({
          id: message.id,
          content: message.content,
          sender: {
            id: message.senderId,
            name: message.senderName,
            avatarUrl: message.senderAvatarUrl,
          },
          createdAt: message.createdAt,
          channelId: message.channelId,
        });
      } else if (data.type === "typing") {
        const typingEvent: TypingEvent = data.payload;
        if (typingEvent.channelId === activeChannelId) {
          handleTypingEvent(typingEvent);
        }
      }
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    socket.onclose = () => {
      console.log("WebSocket disconnected");
      // Attempt to reconnect after a delay
      setTimeout(() => {
        connectWebSocket();
      }, 3000);
    };
  };

  const fetchChannels = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.CHAT.GET_ALL_CHANNELS, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setChannels(data);

        // Set first channel as active if there's no active channel
        if (data.length > 0 && !activeChannelId) {
          setActiveChannelId(data[0].id);
        }
      }
    } catch (error) {
      console.error("Error fetching channels:", error);
    }
  };

  const fetchChannelMessages = async (channelId: string) => {
    try {
      const response = await fetch(
        API_ENDPOINTS.CHAT.GET_CHANNEL_MESSAGES(channelId),
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const formattedMessages = data.map((message: ApiMessage) => ({
          id: message.id,
          content: message.content,
          sender: {
            id: message.senderId,
            name: message.senderName,
            avatarUrl: message.senderAvatarUrl,
          },
          createdAt: message.createdAt,
          channelId: message.channelId,
        }));
        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const createChannel = async () => {
    if (!newChannelName.trim()) return;

    try {
      const response = await fetch(API_ENDPOINTS.CHAT.CREATE_CHANNEL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          name: newChannelName,
          description: newChannelDescription,
        }),
      });

      if (response.ok) {
        const channel = await response.json();
        setChannels([...channels, channel]);
        setActiveChannelId(channel.id);
        setNewChannelName("");
        setNewChannelDescription("");
        setIsCreatingChannel(false);
      }
    } catch (error) {
      console.error("Error creating channel:", error);
    }
  };

  const sendMessage = async () => {
    if (!messageInput.trim() || !activeChannelId || !user?.id) return;

    try {
      const response = await fetch(API_ENDPOINTS.CHAT.CREATE_MESSAGE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          content: messageInput,
          channelId: activeChannelId,
        }),
      });

      if (response.ok) {
        setMessageInput("");
        sendTypingStatus(false);
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const addMessage = (message: ChatMessage) => {
    setMessages((prevMessages) => [...prevMessages, message]);
  };

  const handleTypingEvent = (event: TypingEvent) => {
    if (event.userId === user?.id) return;

    // Update list of typing users
    setTypingUsers((prev) => {
      const newList = prev.filter((id) => id !== event.userName);
      if (event.isTyping) {
        return [...newList, event.userName];
      }
      return newList;
    });
  };

  const sendTypingStatus = (isTyping: boolean) => {
    if (!socketRef.current || !activeChannelId || !user?.id) return;

    const typingEvent = {
      type: "typing",
      payload: {
        userId: user.id,
        userName: user.name || user.email,
        channelId: activeChannelId,
        isTyping,
      },
    };

    socketRef.current.send(JSON.stringify(typingEvent));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setMessageInput(e.target.value);

    // Send typing status
    if (e.target.value.length > 0) {
      sendTypingStatus(true);

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set new timeout
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingStatus(false);
      }, 2000);
    } else {
      sendTypingStatus(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const formatMessageTime = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
      locale: fr,
    });
  };

  return (
    <AdminLayout>
      <div className="flex flex-col h-full">
        <div className="p-4 border-b">
          <h1 className="text-2xl font-bold">Administration du Chat</h1>
          <p className="text-muted-foreground">
            Gérez les canaux de discussion et les messages
          </p>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Channels Sidebar */}
          <div className="w-64 border-r bg-muted/50 flex flex-col">
            <div className="p-4 flex justify-between items-center">
              <h2 className="font-semibold">Canaux</h2>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsCreatingChannel(true)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-1 p-2">
                {channels.map((channel) => (
                  <Button
                    key={channel.id}
                    variant={
                      activeChannelId === channel.id ? "secondary" : "ghost"
                    }
                    className="w-full justify-start"
                    onClick={() => setActiveChannelId(channel.id)}
                  >
                    <span className="truncate"># {channel.name}</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col">
            {activeChannelId ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold">
                      # {channels.find((c) => c.id === activeChannelId)?.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {channels.find((c) => c.id === activeChannelId)
                        ?.description || "Aucune description"}
                    </p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${
                          message.sender.id === user?.id
                            ? "justify-end"
                            : "justify-start"
                        }`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg p-3 ${
                            message.sender.id === user?.id
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted"
                          }`}
                        >
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="font-medium text-sm">
                              {message.sender.name}
                            </span>
                            <span className="text-xs opacity-70">
                              {formatMessageTime(message.createdAt)}
                            </span>
                          </div>
                          <p>{message.content}</p>
                        </div>
                      </div>
                    ))}
                    {typingUsers.length > 0 && (
                      <div className="text-sm text-muted-foreground italic">
                        {typingUsers.join(", ")}{" "}
                        {typingUsers.length === 1
                          ? "est en train d'écrire..."
                          : "sont en train d'écrire..."}
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {/* Message Input */}
                <div className="p-4 border-t">
                  <div className="flex space-x-2">
                    <textarea
                      placeholder="Écrivez un message..."
                      value={messageInput}
                      onChange={handleInputChange}
                      className="flex-1 min-h-[60px] max-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                    />
                    <Button onClick={sendMessage} className="self-end">
                      <Send className="h-4 w-4 mr-2" />
                      Envoyer
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <h3 className="text-lg font-medium mb-2">
                    Sélectionnez un canal ou créez-en un nouveau
                  </h3>
                  <p className="text-muted-foreground">
                    Cliquez sur le bouton + pour créer un nouveau canal
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal for creating a new channel */}
        {isCreatingChannel && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">
                Créer un nouveau canal
              </h3>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="channel-name" className="text-sm font-medium">
                    Nom du canal
                  </label>
                  <input
                    id="channel-name"
                    type="text"
                    placeholder="Nom du canal"
                    value={newChannelName}
                    onChange={(e) => setNewChannelName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div className="space-y-2">
                  <label
                    htmlFor="channel-description"
                    className="text-sm font-medium"
                  >
                    Description
                  </label>
                  <textarea
                    id="channel-description"
                    placeholder="Description du canal"
                    value={newChannelDescription}
                    onChange={(e) => setNewChannelDescription(e.target.value)}
                    className="w-full min-h-[80px] px-3 py-2 border rounded-md"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setIsCreatingChannel(false)}
                >
                  Annuler
                </Button>
                <Button onClick={createChannel}>Créer</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminChat;
